import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Twitter, Send, Menu, X } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const TWITTER = import.meta.env.VITE_TWITTER_URL || 'https://x.com/honeybadgercoin';
const TELEGRAM = import.meta.env.VITE_TELEGRAM_URL || 'https://t.me/honeybadgercoin';
const BUY_URL = import.meta.env.VITE_BUY_URL || 'https://raydium.io/swap/?inputMint=sol&outputMint=BADGER_CONTRACT_ADDRESS_PLACEHOLDER';

const NAV_LINKS = [
  { label: 'Home', href: '#hero', mobile: true },
  { label: 'About', href: '#about', mobile: true },
  { label: 'Intel', href: '#intel', mobile: false },
  { label: 'Memes', href: '#memes', mobile: true },
  { label: 'Raid', href: '#raid', mobile: false },
] as const;

const SOCIALS = [
  { href: TWITTER, Icon: Twitter, label: 'Twitter' },
  { href: TELEGRAM, Icon: Send, label: 'Telegram' },
] as const;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('#hero');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const scrollPoint = window.scrollY + 150;
      let current = '#hero';
      for (const { href } of NAV_LINKS) {
        const el = document.querySelector(href) as HTMLElement | null;
        if (!el) continue;
        if (el.offsetTop <= scrollPoint) current = href;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const scrollTo = (id: string) => {
    trackEvent('nav_click', { target: id });
    setIsMenuOpen(false);
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-[999] flex justify-center"
      style={{ paddingTop: scrolled ? 10 : 20 }}
    >
      <div
        className="relative flex w-[95vw] sm:w-auto items-center"
        style={{ filter: 'drop-shadow(0 8px 30px rgba(0,0,0,0.62)) drop-shadow(0 0 18px rgba(212,175,55,0.13))' }}
      >

        <div className="hidden sm:block" style={{
          width: 0, height: 0,
          borderTop: '46px solid transparent',
          borderBottom: '0px solid transparent',
          borderRight: `20px solid ${scrolled ? 'rgba(10,10,10,0.95)' : 'rgba(10,10,10,0.88)'}`,
          flexShrink: 0,
        }} />

        <div
          className="relative flex w-full sm:w-auto items-center gap-1 overflow-hidden rounded-xl sm:rounded-none"
          style={{
            height: 46,
            background: scrolled ? 'rgba(10,10,10,0.95)' : 'rgba(10,10,10,0.88)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderTop: '1px solid rgba(212,175,55,0.3)',
            borderBottom: '1px solid rgba(212,175,55,0.14)',
            padding: '0 6px',
          }}
        >
          <motion.div
            className="pointer-events-none absolute -left-[20%] top-0 h-full w-[34%]"
            animate={{ x: ['-40%', '340%'] }}
            transition={{ duration: 4.2, repeat: Infinity, ease: 'linear', repeatDelay: 1.8 }}
            style={{ background: 'linear-gradient(100deg, transparent 0%, rgba(212,175,55,0.08) 50%, transparent 100%)' }}
          />

          <div className="flex items-center gap-2 px-2 sm:px-3 md:px-4 mr-1 md:mr-2" style={{ borderRight: '1px solid rgba(212,175,55,0.15)' }}>
            <img src="/assets/pfp.png" alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(212,175,55,0.35)' }} />
            <span className="hidden sm:inline" style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.12em', color: '#D4AF37' }}>
              $BADGER
            </span>
          </div>

          {NAV_LINKS.map(({ label, href, mobile }) => (
            <button
              key={label}
              onClick={() => scrollTo(href)}
              className={`relative px-2 sm:px-3 md:px-4 py-2 sm:py-1 transition-all duration-200 ${mobile ? '' : 'hidden sm:block'}`}
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: active === href ? '#D4AF37' : 'rgba(255,255,255,0.5)',
                textTransform: 'uppercase',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#D4AF37'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = active === href ? '#D4AF37' : 'rgba(255,255,255,0.5)'; }}
            >
              {label}
              <span
                className="absolute left-2 right-2 -bottom-[2px] h-[1px] transition-opacity duration-200"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.9), transparent)',
                  opacity: active === href ? 1 : 0,
                }}
              />
            </button>
          ))}

          <div className="hidden md:block" style={{ width: 1, height: 20, background: 'rgba(212,175,55,0.15)', margin: '0 8px' }} />

          {SOCIALS.map(({ href, Icon, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('social_click', { label, location: 'navbar' })}
              className="hidden sm:flex items-center justify-center transition-all duration-200"
              style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.55)', borderRadius: 6 }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = '#D4AF37';
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(212,175,55,0.1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.55)';
                (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
              }}
            >
              <Icon size={14} />
            </a>
          ))}

          <a
            href={BUY_URL}
            target={BUY_URL.startsWith('http') ? '_blank' : undefined}
            rel={BUY_URL.startsWith('http') ? 'noopener noreferrer' : undefined}
            onClick={() => trackEvent('buy_click', { location: 'navbar' })}
            className="ml-auto sm:ml-2 md:ml-3 flex items-center px-3 sm:px-3 md:px-4 transition-all duration-200"
            style={{
              height: 34,
              fontSize: '0.7rem',
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#080808',
              background: 'linear-gradient(135deg, #FFD700, #D4AF37)',
              clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'linear-gradient(135deg, #FFE566, #F0C848)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'linear-gradient(135deg, #FFD700, #D4AF37)'; }}
          >
            <span className="hidden sm:inline">Buy $BADGER</span>
            <span className="sm:hidden">Buy</span>
          </a>
        </div>

        <div className="hidden sm:block" style={{
          width: 0, height: 0,
          borderTop: '46px solid transparent',
          borderBottom: '0px solid transparent',
          borderLeft: `20px solid ${scrolled ? 'rgba(10,10,10,0.95)' : 'rgba(10,10,10,0.88)'}`,
          flexShrink: 0,
        }} />

        {/* Mobile Hamburger Button */}
        <button
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          className="sm:hidden ml-2 flex items-center justify-center transition-all duration-200"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{ width: 44, height: 44, color: 'rgba(255,255,255,0.7)', background: scrolled ? 'rgba(10,10,10,0.95)' : 'rgba(10,10,10,0.88)', backdropFilter: 'blur(24px)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 8 }}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute top-full left-0 right-0 mt-2 mx-4 z-[-1] overflow-hidden sm:hidden"
            style={{
              background: 'rgba(10,10,10,0.98)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: 12,
              boxShadow: '0 12px 40px rgba(0,0,0,0.8)'
            }}
          >
            <div className="flex flex-col p-4 gap-2">
              {NAV_LINKS.map(({ label, href }) => (
                <button
                  key={label}
                  onClick={() => scrollTo(href)}
                  className="text-left w-full py-3 px-4 rounded-md transition-colors"
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: active === href ? '#D4AF37' : 'rgba(255,255,255,0.6)',
                    background: active === href ? 'rgba(212,175,55,0.1)' : 'transparent',
                  }}
                >
                  {label}
                </button>
              ))}
              <div className="h-[1px] w-full my-2" style={{ background: 'rgba(212,175,55,0.15)' }} />
              <div className="flex justify-center gap-6 py-2">
                {SOCIALS.map(({ href, Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('social_click', { label, location: 'mobile_menu' })}
                    style={{ color: 'rgba(255,255,255,0.65)', transition: 'color 0.2s' }}
                  >
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
